import { abrirVisor } from "@/lib/visor";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { CierreAsunto, VolverEnCurso } from "@/components/asuntos/CierreAsunto";
import * as Icons from "lucide-react";
import { STATUS_STYLES, catLabel } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { HoraAviso, horaAvisoLabel } from "@/components/HoraAviso";
import { InsightList } from "@/components/InsightList";import { runMatterAction } from "@/lib/matterActions";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { PersonaChip } from "@/components/personas/Avatar";
import { eur } from "@/lib/gastos";
import { EditarAsunto } from "@/components/EditarAsunto";
import { RecordatorioPuntual } from "@/components/asuntos/RecordatorioPuntual";
import { Sheet } from "@/pages/Familia";

const AVISO_OPTS = [30, 15, 7, 3, 1];

const Field = ({ label, value, strong, color }) =>
  value == null || value === "" ? null : (
    <div className="flex justify-between gap-4 px-4 py-3">
      <span className="text-base text-[#6E7A74]">{label}</span>
      <span
        className={`text-base text-right ${strong ? "font-bold" : "font-medium"}`}
        style={{ color: color || "#1C2421" }}
      >
        {value}
      </span>
    </div>
  );

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="font-heading text-lg font-bold text-[#1C2421] flex items-center gap-2 mt-7 mb-3">
    <Icon size={20} className="text-[#3A6B56]" /> {children}
  </h2>
);

export default function MatterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getMatterById, getDocumentsForMatter, crearRecordatorio, actualizarRecordatorio, eliminarRecordatorio, actualizarAvisos, eliminarAsunto } = useData();
  const matter = getMatterById(id);

  const [offsets, setOffsets] = useState([]);
  const [avisosOn, setAvisosOn] = useState(false);
  const [avisoBusy, setAvisoBusy] = useState(false);
  const [recBusy, setRecBusy] = useState(false);
  const [newRem, setNewRem] = useState({ date: "", text: "" });
  const [editRem, setEditRem] = useState(null); // {id, date, text}
  const [editando, setEditando] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);

  useEffect(() => {
    if (matter) {
      setOffsets(matter.aviso_offsets || []);
      setAvisosOn(!!matter.avisos_activos);
    }
  }, [matter?.id, matter?.aviso_offsets, matter?.avisos_activos]);

  if (!matter) {
    return (
      <div className="text-center py-20">
        <p className="text-lg text-[#6E7A74]">No se ha encontrado el asunto.</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 min-h-[52px] px-6 rounded-xl bg-[#3A6B56] text-white font-semibold"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  const toggleOffset = (o) =>
    setOffsets((prev) => (prev.includes(o) ? prev.filter((x) => x !== o) : [...prev, o]));

  const guardarAvisos = async () => {
    setAvisoBusy(true);
    try {
      await actualizarAvisos(matter.id, { aviso_offsets: offsets, avisos_activos: avisosOn });
      toast.success("Avisos actualizados");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar.");
    } finally { setAvisoBusy(false); }
  };

  const addRem = async () => {
    if (!newRem.date) return toast.error("Elige una fecha para el recordatorio.");
    setRecBusy(true);
    try {
      await crearRecordatorio({ asunto_id: matter.id, reminder_date: newRem.date, reminder_time: newRem.time || "", text: newRem.text });
      setNewRem({ date: "", time: "", text: "" });
      toast.success("Recordatorio añadido");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo añadir.");
    } finally { setRecBusy(false); }
  };

  const saveRem = async () => {
    setRecBusy(true);
    try {
      await actualizarRecordatorio(editRem.id, { reminder_date: editRem.date, reminder_time: editRem.time || "", text: editRem.text });
      setEditRem(null);
      toast.success("Recordatorio actualizado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar.");
    } finally { setRecBusy(false); }
  };

  const delRem = async (rid) => {
    setRecBusy(true);
    try {
      await eliminarRecordatorio(rid);
      toast.success("Recordatorio eliminado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar.");
    } finally { setRecBusy(false); }
  };

  const doDeleteMatter = async () => {
    setDelBusy(true);
    try {
      await eliminarAsunto(matter.id);
      toast.success("Asunto anulado");
      navigate(-1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo anular el asunto.");
      setDelBusy(false);
    }
  };

  const style = STATUS_STYLES[matter.status];
  const Icon = Icons[matter.icon] || Icons.FileText;
  const docs = getDocumentsForMatter(matter);

  return (
    <div className="space-y-2 pb-4">
      <button
        data-testid="detail-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base mb-2 min-h-[44px]"
      >
        <Icons.ChevronLeft size={22} /> Volver
      </button>

      {/* Cabecera */}
      <div
        className={`aldia-fade-up bg-white border border-[#E5DFD5] ${style.leftBorder} border-l-[6px] rounded-2xl p-5 shadow-sm`}
        data-testid={`detail-${matter.id}`}
      >
        <div className="flex items-start gap-4">
          <div
            className="shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: `${style.indicator}1A` }}
          >
            <Icon size={30} style={{ color: style.indicator }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`${style.badgeBg} ${style.badgeText} text-xs font-bold uppercase px-2.5 py-1 rounded-full`}>
                {style.label}
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#4A5550] bg-[#F2EDE4] rounded-full pl-1 pr-2.5 py-1" data-testid="asunto-persona">
                {matter.family_member_id ? <PersonaChip id={matter.family_member_id} /> : <><Icons.User size={13} /> {matter.person}</>}
              </span>
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#1C2421] mt-2 leading-tight">
              {matter.title}
            </h1>
            <p className="text-base text-[#6E7A74] mt-1">{matter.summary}</p>
            {matter.entrada === "manual" && (
              <button data-testid="asunto-editar-btn" onClick={() => setEditando(true)} className="mt-3 min-h-[44px] px-4 rounded-xl border border-[#E5DFD5] bg-white font-semibold text-[#3A6B56] inline-flex items-center gap-2">
                <Icons.Pencil size={16} /> {matter.recordatorio_puntual ? "Editar recordatorio" : "Editar título, categoría y notas"}
              </button>
            )}
            {matter.recordatorio_puntual && matter.status === "HECHO" && <VolverEnCurso m={matter} testid="asunto-volver-en-curso" className="mt-3 w-full" />}
            {editando && (matter.recordatorio_puntual
              ? <Sheet onClose={() => setEditando(false)} testid="recordatorio-editar"><RecordatorioPuntual matter={matter} onDone={() => setEditando(false)} onCancel={() => setEditando(false)} /></Sheet>
              : <EditarAsunto matter={matter} onClose={() => setEditando(false)} />)}
            {matter.plan_id && <button data-testid="asunto-ver-plan" onClick={() => navigate("/ocio")} className="mt-3 min-h-[44px] px-4 rounded-xl border border-[#E5DFD5] font-semibold text-[#3A6B56]">Ver en Ocio y Reservas</button>}
          </div>
        </div>
      </div>

      {/* Datos del asunto */}
      <SectionTitle icon={Icons.Info}>Información del asunto</SectionTitle>
      <div className="bg-white border border-[#E5DFD5] rounded-2xl divide-y divide-[#F2EDE4]">
        <Field label="Persona relacionada" value={matter.person} />
        <Field label="Categoría" value={catLabel(matter.category)} />
        <Field label="Proveedor" value={matter.provider} />
        <Field label="Relacionado con" value={matter.relatedTo} />
        <Field label="Estado" value={style.label} strong color={style.indicator} />
        <Field label={["PASADO", "HECHO"].includes(matter.status) ? "Fecha" : "Fecha próxima"} value={matter.due_label} strong />
        {matter.amount != null && <Field label="Importe actual" value={eur(matter.amount)} strong />}
        {matter.previous_amount != null && <Field label="Importe anterior" value={eur(matter.previous_amount)} />}
        {matter.priceDiff && <Field label="Variación" value={matter.priceDiff} strong color="#B91C1C" />}
        {matter.autoRenew != null && (
          <Field label="Renovación automática" value={matter.autoRenew ? "Sí" : "No"} />
        )}
      </div>

      {/* Lectura de AL DÍA, diferenciada */}
      <SectionTitle icon={Icons.Sparkles}>Lo que ha visto AL DÍA</SectionTitle>
      <InsightList insights={matter.insights} />

      {/* Próximos pasos */}
      {matter.steps?.length > 0 && (
        <>
          <SectionTitle icon={Icons.ListChecks}>Próximos pasos</SectionTitle>
          <div className="bg-white border border-[#E5DFD5] rounded-2xl p-4 space-y-3">
            {matter.steps.map((s, i) => (
              <div key={i} className="flex gap-3 items-start">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#E8F0EC] text-[#3A6B56] text-sm font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <p className="text-base text-[#1C2421] leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Documentos asociados */}
      <SectionTitle icon={Icons.FileText}>Documentos asociados</SectionTitle>
      {docs.length > 0 ? (
        <div className="space-y-2.5">
          {docs.map((d) => {
            const DIcon = d.type === "Imagen" ? Icons.Image : Icons.FileText;
            return (
              <button
                key={d.id}
                data-testid={`detail-doc-${d.id}`}
                onClick={() =>
                  matter.plan_id ? abrirVisor({ id: d.id, title: d.filename }) : toast.info(d.filename, {
                    description: `${catLabel(d.category)} · ${d.size} · ${d.date_label}`,
                  })
                }
                className="w-full flex items-center gap-3 bg-white border border-[#E5DFD5] rounded-xl p-3.5 text-left hover:border-[#3A6B56] transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#E8F0EC] flex items-center justify-center shrink-0">
                  <DIcon size={20} className="text-[#3A6B56]" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-base text-[#1C2421] truncate">{d.filename}</p>
                  <p className="text-sm text-[#6E7A74]">{d.size} · {d.date_label}</p>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-base text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-xl p-4">
          Aún no hay documentos asociados a este asunto.
        </p>
      )}

      {/* Recordatorios (editable) */}
      <SectionTitle icon={Icons.BellRing}>Recordatorios</SectionTitle>
      <div className="space-y-2.5" data-testid="matter-reminders">
        {(matter.reminders || []).map((r, i) => (
          <div key={r.id || i} data-testid={`reminder-${r.id || i}`} className="bg-white border border-[#E5DFD5] rounded-xl p-3.5">
            {editRem && editRem.id === r.id ? (
              <div className="space-y-2.5">
                <input type="date" data-testid="reminder-edit-date" value={editRem.date}
                  onChange={(e) => setEditRem({ ...editRem, date: e.target.value })} className={smallInput} />
                <HoraAviso value={editRem.time} onChange={(t) => setEditRem({ ...editRem, time: t })} testid="reminder-edit-hora" />
                <input data-testid="reminder-edit-text" value={editRem.text} placeholder="Texto del aviso"
                  onChange={(e) => setEditRem({ ...editRem, text: e.target.value })} className={smallInput} />
                <div className="flex gap-2">
                  <button onClick={() => setEditRem(null)} className="flex-1 min-h-[44px] rounded-lg bg-[#F2EDE4] font-semibold text-sm border border-[#E5DFD5]">Cancelar</button>
                  <button data-testid="reminder-edit-save" onClick={saveRem} disabled={recBusy} className="flex-1 min-h-[44px] rounded-lg bg-[#3A6B56] text-white font-semibold text-sm disabled:opacity-60">Guardar</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {r.status === "Cancelado" ? <Icons.BellOff size={20} className="text-[#9AA39D] shrink-0" /> : <Icons.Bell size={20} className="text-[#D97706] shrink-0" />}
                <div className={`flex-1 min-w-0 ${r.status === "Cancelado" ? "opacity-70" : ""}`}>
                  <p className="font-semibold text-base text-[#1C2421]">{r.text}</p>
                  <p className="text-sm text-[#6E7A74]" data-testid={`reminder-cuando-${r.id}`}>{r.date_label}{r.reminder_date ? ` · ${horaAvisoLabel(r.reminder_time)}` : ""}</p>
                  {r.status === "Cancelado" && <p className="text-sm font-semibold text-[#6E7A74]" data-testid={`reminder-cancelado-${r.id}`}>No se enviará: asunto hecho</p>}
                </div>
                {r.id && r.status !== "Cancelado" && (
                  <>
                    <button data-testid={`reminder-edit-${r.id}`} onClick={() => setEditRem({ id: r.id, date: r.reminder_date || "", time: r.reminder_time || "", text: r.text || "" })}
                      className="w-9 h-9 rounded-lg bg-[#F2EDE4] flex items-center justify-center hover:bg-[#E5DFD5]">
                      <Icons.Pencil size={16} className="text-[#4A5550]" />
                    </button>
                    <button data-testid={`reminder-delete-${r.id}`} onClick={() => delRem(r.id)}
                      className="w-9 h-9 rounded-lg bg-[#FEF2F2] flex items-center justify-center hover:bg-[#FEE2E2]">
                      <Icons.Trash2 size={16} className="text-[#B91C1C]" />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
        {(matter.reminders || []).length === 0 && (
          <p className="text-base text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-xl p-4">
            No hay recordatorios programados.
          </p>
        )}
        <div className="bg-[#F6F3EE] rounded-xl p-3.5 space-y-2.5" data-testid="reminder-add">
          <p className="font-semibold text-sm text-[#4A5550]">Añadir recordatorio</p>
          <input type="date" data-testid="reminder-new-date" value={newRem.date}
            onChange={(e) => setNewRem({ ...newRem, date: e.target.value })} className={smallInput} />
          <HoraAviso value={newRem.time || ""} onChange={(t) => setNewRem({ ...newRem, time: t })} testid="reminder-new-hora" />
          <input data-testid="reminder-new-text" value={newRem.text} placeholder="Texto del aviso (opcional)"
            onChange={(e) => setNewRem({ ...newRem, text: e.target.value })} className={smallInput} />
          <button data-testid="reminder-new-save" onClick={addRem} disabled={recBusy}
            className="w-full min-h-[48px] rounded-lg bg-[#3A6B56] text-white font-semibold text-base disabled:opacity-60">
            Añadir recordatorio
          </button>
        </div>
      </div>

      {/* Avisos por email antes del vencimiento */}
      <SectionTitle icon={Icons.MailCheck}>Avisos por email</SectionTitle>
      <div className="bg-white border border-[#E5DFD5] rounded-2xl p-4 space-y-3" data-testid="matter-avisos">
        {!matter.due_date && (
          <p className="text-sm text-[#B45309] bg-[#FEF3C7] rounded-lg p-3">
            Este asunto no tiene fecha de vencimiento, así que no se enviarán avisos.
          </p>
        )}
        <p className="text-sm text-[#6E7A74]">Elige cuándo quieres recibir un email antes del vencimiento. Puedes elegir varios.</p>
        <div className="flex flex-wrap gap-2">
          {AVISO_OPTS.map((o) => (
            <button key={o} type="button" data-testid={`aviso-${o}`} onClick={() => toggleOffset(o)}
              className={`min-h-[44px] px-4 rounded-full font-semibold text-sm border transition-colors ${
                offsets.includes(o) ? "bg-[#3A6B56] text-white border-[#3A6B56]" : "bg-white text-[#4A5550] border-[#E5DFD5] hover:border-[#3A6B56]"
              }`}>
              {o === 1 ? "1 día antes" : `${o} días antes`}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-3 cursor-pointer pt-1">
          <input type="checkbox" data-testid="aviso-activos" checked={avisosOn}
            onChange={(e) => setAvisosOn(e.target.checked)} className="w-5 h-5 accent-[#3A6B56]" />
          <span className="text-base text-[#1C2421]">Avisos activados</span>
        </label>
        <button data-testid="aviso-guardar" onClick={guardarAvisos} disabled={avisoBusy}
          className="w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] disabled:opacity-60">
          {avisoBusy ? "Guardando…" : "Guardar avisos"}
        </button>
        <p className="text-xs text-[#6E7A74]">
          Para desactivarlos, quita todas las opciones o desmarca «Avisos activados» y guarda. No enviamos emails repetidos.
        </p>
      </div>


      {/* Historial */}
      <SectionTitle icon={Icons.History}>Historial del asunto</SectionTitle>
      <div className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
        <ol className="relative border-l-2 border-[#E5DFD5] ml-2 space-y-5">
          {matter.history.map((h, i) => (
            <li key={i} className="ml-5">
              <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-[#3A6B56]" />
              <p className="text-sm font-semibold text-[#6E7A74]">{h.date_label}</p>
              <p className="text-base text-[#1C2421] leading-relaxed">{h.text}</p>
            </li>
          ))}
        </ol>
      </div>

      <SectionTitle icon={Icons.CheckCircle2}>Estado</SectionTitle>
      <CierreAsunto m={matter} abrirFecha={new URLSearchParams(location.search).get("nueva") === "1"} />

      {/* Acciones específicas */}
      <SectionTitle icon={Icons.Zap}>Acciones</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {matter.actions
          .filter((a) => !["Ver detalle", "Ver opciones", "Ver cita", "Hecho", "Nueva fecha"].includes(a))
          .concat(["Preparar correo", "Recordármelo"].filter((a) => !matter.actions.includes(a) && !["BAJO CONTROL", "PASADO", "HECHO"].includes(matter.status)))
          .map((action, i) => (
            <button
              key={action}
              data-testid={`detail-action-${toSlug(action)}`}
              onClick={() => runMatterAction(action, matter, navigate)}
              className={
                i === 0
                  ? "min-h-[56px] px-5 rounded-xl font-semibold text-base bg-[#3A6B56] text-white hover:bg-[#2E5645] transition-colors"
                  : "min-h-[56px] px-5 rounded-xl font-semibold text-base bg-[#F2EDE4] text-[#1C2421] border border-[#E5DFD5] hover:bg-[#E5DFD5] transition-colors"
              }
            >
              {action}
            </button>
          ))}
      </div>

      <div className="mt-6 flex items-start gap-2 bg-[#F6F3EE] rounded-xl p-4">
        <Icons.ShieldCheck size={18} className="text-[#3A6B56] shrink-0 mt-0.5" />
        <p className="text-sm text-[#6E7A74] leading-relaxed">
          AL DÍA no ejecuta ninguna acción importante sin tu confirmación expresa.
          Las recomendaciones administrativas no sustituyen el consejo profesional.
        </p>
      </div>

      {/* Anular (eliminar) el asunto */}
      <button
        data-testid="detail-delete-matter"
        onClick={() => setConfirmDel(true)}
        className="mt-4 w-full min-h-[52px] rounded-xl bg-[#FEF2F2] text-[#B91C1C] font-semibold text-base border border-[#FECACA] hover:bg-[#FEE2E2] transition-colors flex items-center justify-center gap-2"
      >
        <Icons.Trash2 size={18} /> Anular este asunto
      </button>

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="matter-confirm-delete">
          <div className="absolute inset-0 bg-[#1C2421]/40 backdrop-blur-sm" onClick={() => !delBusy && setConfirmDel(false)} />
          <div className="relative w-full sm:max-w-md bg-[#FAF8F5] rounded-t-3xl sm:rounded-3xl p-6 pb-8 shadow-2xl aldia-fade-up">
            <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Anular «{matter.title}»?</h2>
            <p className="text-base text-[#6E7A74] mt-2">
              Se eliminará este asunto y sus recordatorios asociados. Los documentos que hayas subido no se borran.
              Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-2.5 mt-5">
              <button
                onClick={() => setConfirmDel(false)}
                disabled={delBusy}
                className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5] disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                data-testid="matter-confirm-delete-btn"
                onClick={doDeleteMatter}
                disabled={delBusy}
                className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60"
              >
                {delBusy ? "Anulando…" : "Anular asunto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const smallInput =
  "w-full min-h-[44px] px-3 rounded-lg bg-white border border-[#E5DFD5] text-base text-[#1C2421] placeholder-[#9AA39D] focus:outline-none focus:ring-2 focus:ring-[#3A6B56]/30 focus:border-[#3A6B56]";

function toSlug(s) {
  return s
    .toLowerCase()
    .replace(/[áàä]/g, "a").replace(/[éèë]/g, "e").replace(/[íìï]/g, "i")
    .replace(/[óòö]/g, "o").replace(/[úùü]/g, "u").replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
